(() => {
if (customElements.get('auto-scrolling-products')) return;

class AutoScrollingProducts extends HTMLElement {
  constructor() {
    super();
    this.frame = null;
    this.lastFrameTime = null;
    this.rebuildFrame = null;
    this.releaseTimer = null;
    this.pauseReasons = new Set();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.tick = this.tick.bind(this);
    this.queueRebuild = this.queueRebuild.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.stage = this.querySelector('[data-carousel-stage]');
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = Math.max(Number.parseFloat(this.dataset.speed) || 20, 1);
    this.direction = this.dataset.direction === 'right' ? 'right' : 'left';

    if (!this.track) return;

    this.originalSlides = Array.from(this.track.querySelectorAll('[data-carousel-slide]:not([data-carousel-clone])'));
    if (this.originalSlides.length < 2) return;

    this.previousHandler = () => this.moveByCard(-1);
    this.nextHandler = () => this.moveByCard(1);
    this.pointerDownHandler = () => this.pause('pointer');
    this.pointerUpHandler = () => this.releaseAfter('pointer', 900);
    this.wheelHandler = () => this.releaseAfter('wheel', 1200, true);
    this.focusInHandler = () => this.pause('focus');
    this.focusOutHandler = (event) => {
      if (!this.contains(event.relatedTarget)) this.resume('focus');
    };
    this.mouseEnterHandler = () => this.pause('hover');
    this.mouseLeaveHandler = () => this.resume('hover');
    this.motionHandler = () => this.updatePlayback();

    this.previousButton?.addEventListener('click', this.previousHandler);
    this.nextButton?.addEventListener('click', this.nextHandler);
    this.track.addEventListener('pointerdown', this.pointerDownHandler, { passive: true });
    window.addEventListener('pointerup', this.pointerUpHandler, { passive: true });
    this.track.addEventListener('touchend', this.pointerUpHandler, { passive: true });
    this.track.addEventListener('wheel', this.wheelHandler, { passive: true });
    this.addEventListener('focusin', this.focusInHandler);
    this.addEventListener('focusout', this.focusOutHandler);

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', this.mouseEnterHandler);
      this.addEventListener('mouseleave', this.mouseLeaveHandler);
    }

    this.reducedMotion.addEventListener('change', this.motionHandler);
    document.addEventListener('visibilitychange', this.handleVisibility);

    this.resizeObserver = new ResizeObserver(this.queueRebuild);
    this.resizeObserver.observe(this.stage || this);
    this.rebuild();
  }

  disconnectedCallback() {
    this.stop();
    if (this.rebuildFrame) cancelAnimationFrame(this.rebuildFrame);
    clearTimeout(this.releaseTimer);
    this.resizeObserver?.disconnect();
    this.previousButton?.removeEventListener('click', this.previousHandler);
    this.nextButton?.removeEventListener('click', this.nextHandler);
    this.track?.removeEventListener('pointerdown', this.pointerDownHandler);
    window.removeEventListener('pointerup', this.pointerUpHandler);
    this.track?.removeEventListener('touchend', this.pointerUpHandler);
    this.track?.removeEventListener('wheel', this.wheelHandler);
    this.removeEventListener('focusin', this.focusInHandler);
    this.removeEventListener('focusout', this.focusOutHandler);
    this.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.removeEventListener('mouseleave', this.mouseLeaveHandler);
    this.reducedMotion.removeEventListener('change', this.motionHandler);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  queueRebuild() {
    if (this.rebuildFrame) return;
    this.rebuildFrame = requestAnimationFrame(() => {
      this.rebuildFrame = null;
      this.rebuild();
    });
  }

  rebuild() {
    if (!this.track || this.originalSlides.length < 2) return;

    const wasRunning = Boolean(this.frame);
    this.stop();
    this.track.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
    this.track.scrollLeft = 0;

    const firstCloneSet = this.appendCloneSet();
    const firstOriginal = this.originalSlides[0];
    const firstClone = firstCloneSet[0];
    this.cycleWidth = firstClone.offsetLeft - firstOriginal.offsetLeft;

    let safety = 0;
    while (this.track.scrollWidth < this.cycleWidth + this.track.clientWidth * 2 && safety < 8) {
      this.appendCloneSet();
      safety += 1;
    }

    if (this.direction === 'right') this.track.scrollLeft = this.cycleWidth;
    this.virtualPosition = this.track.scrollLeft;
    this.positionControls();
    this.lastFrameTime = null;

    if (wasRunning || this.shouldPlay) this.start();
  }

  appendCloneSet() {
    return this.originalSlides.map((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.carouselClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      this.track.appendChild(clone);
      return clone;
    });
  }

  positionControls() {
    const media = this.originalSlides[0]?.querySelector('.auto-product-card__media');
    if (!media || !this.stage) return;
    this.stage.style.setProperty('--auto-products-control-y', `${media.offsetTop + media.offsetHeight / 2}px`);
  }

  get shouldPlay() {
    return this.autoplay && !this.reducedMotion.matches && this.pauseReasons.size === 0 && !document.hidden;
  }

  updatePlayback() {
    this.track?.setAttribute('aria-live', this.shouldPlay ? 'off' : 'polite');
    if (this.shouldPlay) {
      this.start();
    } else {
      this.stop();
    }
  }

  start() {
    if (this.frame || !this.shouldPlay || !this.cycleWidth) return;
    this.lastFrameTime = null;
    this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.lastFrameTime = null;
  }

  tick(time) {
    this.frame = null;
    if (!this.shouldPlay) return;

    if (this.lastFrameTime !== null) {
      const elapsed = Math.min(time - this.lastFrameTime, 64) / 1000;
      const distance = this.speed * elapsed * (this.direction === 'right' ? -1 : 1);
      this.virtualPosition += distance;
      this.normalisePosition();
    }

    this.lastFrameTime = time;
    this.frame = requestAnimationFrame(this.tick);
  }

  normalisePosition() {
    if (!this.cycleWidth) return;
    while (this.virtualPosition >= this.cycleWidth) this.virtualPosition -= this.cycleWidth;
    while (this.virtualPosition <= 0.5 && this.direction === 'right') this.virtualPosition += this.cycleWidth;
    this.track.scrollLeft = this.virtualPosition;
  }

  moveByCard(direction) {
    if (!this.originalSlides.length) return;
    this.pause('manual');

    const first = this.originalSlides[0];
    const second = this.originalSlides[1];
    const step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;

    if (direction < 0 && this.track.scrollLeft < step) {
      this.track.scrollLeft += this.cycleWidth;
    }

    this.track.scrollBy({ left: step * direction, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    this.releaseAfter('manual', 900);
  }

  pause(reason) {
    this.virtualPosition = this.track?.scrollLeft || 0;
    this.pauseReasons.add(reason);
    this.updatePlayback();
  }

  resume(reason) {
    this.pauseReasons.delete(reason);
    this.virtualPosition = this.track?.scrollLeft || 0;
    this.normalisePosition();
    this.updatePlayback();
  }

  releaseAfter(reason, delay, addReason = false) {
    if (addReason) this.pause(reason);
    clearTimeout(this.releaseTimer);
    this.releaseTimer = setTimeout(() => this.resume(reason), delay);
  }

  handleVisibility() {
    this.updatePlayback();
  }
}

if (!customElements.get('auto-scrolling-products')) {
  customElements.define('auto-scrolling-products', AutoScrollingProducts);
}
})();
